<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import {
  useContextStore,
  usePeopleStore,
  useToastStore,
} from "../data/store.ts";
import ToolTip from "../components/ToolTip.vue";
import Legend from "../components/LegendContainer.vue";
import type { Person, BookingMode, Booking } from "../data/types.d.ts";
import { desks, deskLookup } from "../data/rooms.ts";
import { holidays } from "../data/holidays.ts";
import { d2s, DELETE, GET, POST } from "../data/api.ts";
import GreeterBox from "../components/GreeterBox.vue";

const LOOK_BACK = 2;
const FUTURE_STEPS = 21;

const contextStore = useContextStore();
const toaster = useToastStore();
const peopleStore = usePeopleStore();

const startDate = new Date();
const endDate = new Date();
const numDays = ref(FUTURE_STEPS);
startDate.setDate(startDate.getDate() - LOOK_BACK);
endDate.setDate(endDate.getDate() + FUTURE_STEPS);

const bookingMode = ref<BookingMode>("FULL_DAY");

const showSettings = ref(false);

const bookings = ref<Booking[]>([]);
const table = computed(() => {
  // @ts-ignore
  const ret = Object.fromEntries(
    [...Array(numDays.value)].map(
      (
        //@ts-ignore
        v,
        i,
      ) => {
        const now = new Date();
        const day = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + i - 1,
        );
        return [
          day.toDateString(),
          {
            day: day,
            isWeekday: (day.getDay() + 6) % 7 <= 4,
            isToday: day.toDateString() == now.toDateString(),
            isHoliday: !!holidays[day.toDateString()],
            isMonday: day.getDay() === 1,
            holiday: holidays[day.toDateString()]?.name,
            desks: Object.fromEntries(
              Object.keys(deskLookup).map((key) => [
                key,
                {
                  icon: ICONS.UNDEFINED,
                  colours: [] as string[],
                  acronyms: [] as Person[],
                  bookings: [] as Booking[],
                },
              ]),
            ),
          },
        ];
      },
    ),
  );

  // add bookings to table
  bookings.value.forEach((booking) => {
    ret[booking.day.toDateString()]?.desks[booking.desk]?.bookings.push(
      booking,
    );
  });

  // resolve icons and co in second pass
  Object.values(ret).forEach((column) =>
    Object.values(column.desks).forEach((desk) => {
      if (desk.bookings.length > 0) {
        if (desk.bookings.some((b) => b.person === contextStore.user)) {
          // at least one booking is mine
          desk.colours = ["my-booking"];
        } else {
          if (peopleStore.people) {
            desk.colours = desk.bookings.map((b) => {
              return {
                STUD: "booking-stud",
                FULL: "booking-full",
                GUEST: "booking-guest",
                // @ts-ignore
              }[peopleStore.people[b.person]?.type];
            });
          }
        }
        if (desk.bookings.some((b) => b.type === "FULL_DAY")) {
          desk.icon = ICONS.FULL_DAY;
        } else if (desk.bookings.some((b) => b.type === "HALF_DAY")) {
          desk.icon = ICONS.HALF_DAY;
        } else {
          desk.icon = ICONS.FREE;
        }

        // @ts-ignore
        desk.acronyms = desk.bookings
          .map((b) => {
            if (b.type === "FREE" || !peopleStore.people) return;
            return peopleStore.people[b.person];
          })
          .filter((a) => !!a);
      }
    }),
  );

  return ret;
});

function moreEarlier() {
  numDays.value += FUTURE_STEPS;
  startDate.setDate(startDate.getDate() - FUTURE_STEPS);
  fetchBookings();
}

function moreLater() {
  numDays.value += FUTURE_STEPS;
  endDate.setDate(endDate.getDate() + FUTURE_STEPS);
  fetchBookings();
}

const ICONS = {
  FULL_DAY: "bi-calendar-x-fill",
  HALF_DAY: "bi-calendar-week",
  FREE: "bi-calendar-check",
  UNDEFINED: "bi-calendar",
};

function bookDesk(day: Date, desk: number, event: MouseEvent) {
  const newBooking: Booking = {
    id: crypto.randomUUID(),
    day,
    desk,
    person: contextStore.user as string,
    // @ts-ignore
    type: bookingMode.value,
  };

  function doBooking(succ: string, fail: string) {
    // for some immediate feedback, push this.
    const newIdx = bookings.value.push(newBooking);

    POST("/booking/", { ...newBooking, day: d2s(newBooking.day) })
      .then(() => {
        toaster.toast(succ, "SUCC");
      })
      .catch(() => {
        // remove the booking that failed
        bookings.value.splice(newIdx - 1, 1);
        toaster.toast(fail, "FAIL");
      });
  }

  function clearBooking(bid: string, info: boolean) {
    const delIdx = bookings.value.map((b) => b.id).indexOf(bid);
    const memory = bookings.value[delIdx];

    // drop for some immediate feedback
    bookings.value.splice(delIdx, 1);
    DELETE("/booking/", { bid })
      .then(() => {
        if (info) toaster.toast("Dropped reservation.", "SUCC");
      })
      .catch(() => {
        // add failed to drop booking back in
        bookings.value.push(memory);
        if (info) toaster.toast("Failed to drop reservation.", "WARN");
      });
  }

  const existingBookings = table.value[day.toDateString()].desks[desk].bookings;
  const myBookings = (existingBookings ?? []).filter(
    (booking) => booking.person === contextStore.user,
  );
  const blockedByOther = (existingBookings ?? []).some(
    (booking) =>
      booking.person !== contextStore.user && booking.type === "FULL_DAY",
  );

  if (bookingMode.value === "CLEAR") {
    // clear all bookings for that day/desk/person
    myBookings.forEach((booking) => clearBooking(booking.id, true));
  } else if (bookingMode.value === "FREE") {
    // clear all if needed
    myBookings.forEach((booking) => clearBooking(booking.id, false));

    // give some reward
    confetti(event);

    doBooking("Thanks for freeing the desk!", "Failed to mark desk as free.");
  } else if (bookingMode.value === "FULL_DAY") {
    // in case user has half-day or free bookings, seems like the intention is to invert the booking -> clear first
    myBookings.forEach((booking) => {
      if (booking.type !== "FULL_DAY") clearBooking(booking.id, false);
    });

    if (myBookings.some((booking) => booking.type === "FULL_DAY")) {
      // already have a full-day reservation; stop here.
      return;
    }

    if (!blockedByOther) {
      doBooking(
        `Reserved desk on ${day.toDateString()}`,
        `Failed to book desk on ${day.toDateString()}`,
      );
    } else {
      toaster.toast("Desk already reserved, sorry.", "WARN");
    }
  } else if (bookingMode.value === "HALF_DAY") {
    // clear all if needed
    myBookings.forEach((booking) => clearBooking(booking.id, false));
    doBooking("Made part-day reservation", "Failed to make reservation");
  }
}

function modeHotkeyListener(event: KeyboardEvent) {
  switch (event.code) {
    case "KeyF":
      bookingMode.value = "FREE";
      break;
    case "KeyC":
      bookingMode.value = "CLEAR";
      break;
    case "KeyP":
    case "KeyH":
      bookingMode.value = "HALF_DAY";
      break;
    case "KeyB":
      bookingMode.value = "FULL_DAY";
      break;
  }
}

function confetti(e: MouseEvent) {
  // https://www.cssscript.com/demo/celebration-fireworks-confetti/
  // https://www.cssscript.com/demo/bubble-confetti-effect-button/
  e.preventDefault();

  const targets = e
    .composedPath()
    .filter((el) => (el as HTMLElement).classList?.contains("desk-cell"));
  if (targets.length > 0) {
    const target = targets[0] as HTMLDivElement;
    target.classList.remove("animate");
    target.classList.add("animate");
    setTimeout(function () {
      target.classList.remove("animate");
    }, 1200);
  }
}

function fetchBookings() {
  GET<Booking[]>("/bookings/", { start: d2s(startDate), end: d2s(endDate) })
    .then((bookings_) => {
      bookings.value = bookings_.map((b) => ({
        ...b,
        day: new Date(b.day),
        // begin: new Date(b.day),
        // end: new Date(b.day),
      }));
    })
    .catch(() => {
      toaster.toast("Failed to load bookings", "WARN");
    });
}

onMounted(() => {
  window.addEventListener("keydown", modeHotkeyListener);
  fetchBookings();
});

onUnmounted(() => {
  window.removeEventListener("keydown", modeHotkeyListener);
});
</script>

<template>
  <template v-if="peopleStore.people === null">
    <h1>Hi!</h1>
    <p class="lead">Please wait while data is loaded...</p>
  </template>

  <template v-else>
    <GreeterBox>
      Reserve a desk in the table below. Please also remember to mark your desk
      as free!
      <template #icons>
        <i class="bi-gear icon-btn" @click="showSettings = !showSettings" />
      </template>
    </GreeterBox>

    <div class="row justify-content-center">
      <div class="col-auto">
        <div class="input-group mb-3 input-group-sm">
          <button
            class="btn btn-outline-secondary"
            type="button"
            @click="moreEarlier"
          >
            <i class="bi-caret-left" />
          </button>

          <button
            class="btn btn-outline-secondary"
            type="button"
            @click="moreLater"
          >
            <i class="bi-caret-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- SETTINGS CARD -->
    <div
      v-if="showSettings"
      class="card mb-4 p-3 pt-2 col-lg-6 col ms-auto me-auto text-start"
    >
      <div class="row">
        <div class="col">
          Select rooms to display
          <ul class="list-unstyled">
            <li
              v-for="room in desks"
              :key="room.name"
              class="d-flex flex-row"
              style="align-items: flex-start"
            >
              <input
                :id="`room-check-${room.name}`"
                v-model="contextStore.selectedRooms"
                type="checkbox"
                :value="room.name"
              />
              <label :for="`room-check-${room.name}`" class="ms-2"
              >{{ room.name }}
                <span class="text-muted ms-2">{{ room.desc }}</span>
              </label>
            </li>
          </ul>
        </div>
        <div class="col">
          <div class="form-check form-switch">
            <input
              id="showWeekends"
              v-model="contextStore.showWeekends"
              class="form-check-input"
              type="checkbox"
              role="switch"
            />
            <label class="form-check-label" for="showWeekends"
            >Show weekends</label
            >
          </div>
        </div>
      </div>
    </div>

    <div class="bookings">
      <!-- Main table -->
      <div v-if="contextStore.user" class="desks">
        <table>
          <thead>
          <tr>
            <th></th>
            <th></th>

            <template v-for="column in table" :key="column.day">
              <th
                v-if="
                    contextStore.showWeekends ||
                    (!contextStore.showWeekends && column.isWeekday)
                  "
                :class="{
                    'col-weekend-day': !column.isWeekday,
                    'col-today': column.isToday,
                    'col-weekday': column.isWeekday,
                    'col-holiday': column.isHoliday,
                    'col-monday': column.isMonday,
                  }"
              >
                <div class="colhead">
                  <div v-if="column.isHoliday" class="icon-holiday">
                    <ToolTip :text="column.holiday" class="mt-2">
                      <i class="bi-building-slash" />
                    </ToolTip>
                  </div>
                  <div v-if="column.isToday" class="icon-today">
                    <i class="bi-caret-down-fill" />
                  </div>
                  <div class="date-day">
                    {{
                      column.day
                        .toLocaleString("default", { weekday: "short" })
                        .slice(0, 2)
                    }}
                  </div>
                  <div class="date-text">
                    {{ column.day.getDate() }}.&nbsp;{{
                      column.day.toLocaleString("default", { month: "short" })
                    }}
                  </div>
                </div>
              </th>
            </template>
          </tr>
          </thead>
          <template v-for="room in desks" :key="room.name">
            <tbody v-if="contextStore.selectedRooms.indexOf(room.name) >= 0">
            <template v-for="(desk, i) in room.desks" :key="desk.id">
              <tr>
                <th v-if="i === 0" class="room" :rowspan="room.desks.length">
                  <ToolTip
                    :text="room.desc ?? ''"
                    class="mt-2"
                    style="white-space: nowrap"
                    tt-class="tt-rotate"
                  >
                    {{ room.name }}
                  </ToolTip>
                </th>
                <th class="desk" :class="{ flex: desk.flex }">
                  <ToolTip class="mt-2" position="right">
                    {{ desk.name }}
                    <template #text>
                      {{ desk.owner ?? "Flex" }}
                      <template v-if="desk.info">
                        <br /><span
                        style="font-style: italic; font-size: 0.75em"
                      >({{ desk.info }})</span
                      >
                      </template>
                    </template>
                  </ToolTip>
                </th>
                <template v-for="column in table" :key="+column.day">
                  <td
                    v-if="
                        contextStore.showWeekends ||
                        (!contextStore.showWeekends && column.isWeekday)
                      "
                    :class="{
                        'col-weekend-day': !column.isWeekday,
                        'col-today': column.isToday,
                        'col-weekday': column.isWeekday,
                        'col-holiday': column.isHoliday,
                        'col-monday': column.isMonday,
                      }"
                  >
                    <div
                      class="mcc-row desk-cell"
                      :class="{
                          'first-in-room': i === 0,
                          'last-in-room': i === room.desks.length - 1,
                        }"
                       @click="bookDesk(column.day, desk.id, $event)"
                    >
                      <template
                        v-if="column.desks[desk.id]?.acronyms.length > 0"
                      >
                        <div>
                          <i
                            class="me-1"
                            :class="[
                                column.desks[desk.id]?.icon,
                                column.desks[desk.id]?.colours,
                              ]"
                          />
                        </div>
                        <div
                          class="d-flex flex-column justify-content-center"
                        >
                          <ToolTip
                            v-for="acronym in column.desks[desk.id]?.acronyms"
                            :key="acronym.id"
                            :text="acronym.name"
                            style="line-height: 0.5rem; height: 0.5rem"
                          >
                            <div style="font-size: 0.5em">
                              {{ acronym.acro }}
                            </div>
                          </ToolTip>
                        </div>
                      </template>
                      <template v-else>
                        <i
                          :class="[
                              column.desks[desk.id]?.icon,
                              column.desks[desk.id]?.colours,
                            ]"
                        />
                      </template>
                    </div>
                  </td>
                </template>
              </tr>
            </template>
            </tbody>
          </template>
        </table>
      </div>
    </div>

    <div class="mt-2 text-muted">
      Mode:
      <i
        class="bi-calendar-check me-1"
        :class="{ 'mode-active': bookingMode == 'FREE' }"
        @click="bookingMode = 'FREE'"
      />
      <i
        class="bi-calendar-x me-1"
        :class="{ 'mode-active': bookingMode == 'FULL_DAY' }"
        @click="bookingMode = 'FULL_DAY'"
      />
      <i
        class="bi-calendar-week me-1"
        :class="{ 'mode-active': bookingMode == 'HALF_DAY' }"
        @click="bookingMode = 'HALF_DAY'"
      />
      <i
        class="bi-calendar me-1"
        :class="{ 'mode-active': bookingMode == 'CLEAR' }"
        @click="bookingMode = 'CLEAR'"
      />
    </div>

    <Legend class="mt-5" />
  </template>
</template>

<style>
.tt-rotate {
  transform: rotate(90deg) translate(-50%, calc(100% + 3em)) !important;
}

th.flex {
  background-color: var(--colour-flex) !important;
}
</style>
<style scoped lang="scss">
.mode-active {
  padding: 2px 4px 2px 3px;
  border: 1px solid #6b6b6b;
  border-radius: 5px;
}

.colhead {
  display: flex;
  flex-direction: column;
  align-items: center;
  /*height: 2.5em;*/
  justify-content: end;
  padding: 0 0.125em;
}

.bookings {
  display: flex;
  flex-direction: row;
  justify-content: center !important;

  & > .desks {
    --width-room-col: 1.5em;
    --width-desk-col: 1.5em;

    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid black;

    table {
      border-collapse: collapse;
    }

    tbody::after {
      content: "";
      display: table-row;
      height: 0.25em;
    }

    thead th,
    tbody th {
      font-weight: inherit;
      font-size: inherit;
    }

    thead th {
      background-color: var(--bg-weekday);
      height: 0;
    }

    tbody tr:first-of-type {
      border-top: 1px solid black;
    }

    tbody tr:last-of-type {
      border-bottom: 1px solid black;
      margin-bottom: 0.25em;
    }

    tbody th {
      background-color: var(--bg-weekday);
      position: -webkit-sticky; /* for Safari */
      position: sticky;

      &.desk {
        width: var(--width-desk-col);
        left: var(--width-room-col);
      }

      &.room {
        width: var(--width-room-col);
        left: 0;
        writing-mode: vertical-lr;
        transform: rotate(180deg);
      }
    }

    td {
      padding: 0;
      margin: 0;
    }

    /* To have the header in the first column stick to the left: */
    thead th:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
    }

    thead th:nth-child(2) {
      position: sticky;
      left: var(--width-room-col);
      z-index: 2;
    }
  }
}

@keyframes bang {
  from {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
}

.desk-cell {
  display: flex;
  transition: font-size ease-in 0.05s;

  &:focus {
    outline: 0;
  }

  &:before,
  &:after {
    position: absolute;
    content: "";
    display: block;
    width: 150%;
    height: 120%;
    left: -20%;
    transition: all ease-in-out 0.5s;
    background-repeat: no-repeat;
  }

  &:before {
    display: none;
    top: -60%;
    background-image: radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #ff0081 20%, transparent 30%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, transparent 10%, #ff0081 15%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%);
    background-size: 10% 10%,
    20% 20%,
    15% 15%,
    20% 20%,
    18% 18%,
    10% 10%,
    15% 15%,
    10% 10%,
    18% 18%;
  }

  &:after {
    display: none;
    bottom: -60%;
    background-image: radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, transparent 10%, #ff0081 15%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%),
    radial-gradient(circle, #ff0081 20%, transparent 20%);
    background-size: 15% 15%,
    20% 20%,
    18% 18%,
    20% 20%,
    15% 15%,
    10% 10%,
    20% 20%;
  }

  &:active {
    font-size: 0.7em;
    box-shadow: 0 2px 25px rgba(255, 0, 130, 0.2);
  }

  &.animate:before,
  &.animate:after {
    display: block;
    z-index: 1000;
  }

  &.animate {
    position: relative;
  }

  &.animate:before {
    animation: topBubbles ease-in-out 1.25s infinite;
  }

  &.animate:after {
    animation: bottomBubbles ease-in-out 1.25s infinite;
  }

  @keyframes topBubbles {
    0% {
      background-position: 5% 90%,
      10% 90%,
      10% 90%,
      15% 90%,
      25% 90%,
      25% 90%,
      40% 90%,
      55% 90%,
      70% 90%;
    }
    50% {
      background-position: 0 80%,
      0 20%,
      10% 40%,
      20% 0,
      30% 30%,
      22% 50%,
      50% 50%,
      65% 20%,
      90% 30%;
    }
    100% {
      background-position: 0 70%,
      0 10%,
      10% 30%,
      20% -10%,
      30% 20%,
      22% 40%,
      50% 40%,
      65% 10%,
      90% 20%;
      background-size: 0 0,
      0 0,
      0 0,
      0 0,
      0 0,
      0 0;
    }
  }

  @keyframes bottomBubbles {
    0% {
      background-position: 10% -10%,
      30% 10%,
      55% -10%,
      70% -10%,
      85% -10%,
      70% -10%,
      70% 0;
    }
    50% {
      background-position: 0 80%,
      20% 80%,
      45% 60%,
      60% 100%,
      75% 70%,
      95% 60%,
      105% 0;
    }
    100% {
      background-position: 0 90%,
      20% 90%,
      45% 70%,
      60% 110%,
      75% 80%,
      95% 70%,
      110% 10%;
      background-size: 0 0,
      0 0,
      0 0,
      0 0,
      0 0,
      0 0;
    }
  }
}
</style>
